import { Badge, Box, Button, Checkbox, Group, Text } from "@mantine/core";
import type { Aufgabe, AufgabeQuelle, AufgabeZiel } from "@msc/shared";
import { Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { formatIsoDate, fromLocalDateString, toLocalDateString } from "../../lib/date.js";

/**
 * Eine Zeile der Aufgabenliste und die Bausteine, die auch die Bereichssicht
 * nutzt. Die API liefert Rohdaten; jeder Satz entsteht hier aus `quelle` und
 * `ziel`.
 */

/** Badge-Farbe je Quelle. `variant="light"` geht mit dem dunklen Modus mit. */
const TAG_FARBE: Record<AufgabeQuelle, string> = {
  todo: "blue",
  besuch: "orange",
  systemstand: "violet",
  statusbericht: "teal",
  freigabe: "green",
  datenluecke: "gray",
};

export function QuelleTag({ quelle }: { quelle: AufgabeQuelle }) {
  const { t } = useTranslation();
  return (
    <Badge variant="light" color={TAG_FARBE[quelle]} miw={104} style={{ flexShrink: 0 }}>
      {t(`aufgaben.tag.${quelle}`)}
    </Badge>
  );
}

function monatName(month: string, lng: string): string {
  const [jahr, monat] = month.split("-").map(Number);
  return new Intl.DateTimeFormat(lng, { month: "long", year: "numeric" }).format(
    new Date(jahr ?? 1970, (monat ?? 1) - 1, 1),
  );
}

/** Was zu tun ist, als ein Satz. */
export function aufgabeText(a: Aufgabe, t: TFunction, lng: string): string {
  switch (a.quelle) {
    case "todo":
      return a.titel ?? "";
    case "besuch":
      return t("aufgaben.text.besuch");
    case "systemstand":
      return t("aufgaben.text.systemstand");
    case "statusbericht":
      return t("aufgaben.text.statusbericht", {
        monat: a.ziel.art === "statusbericht" ? monatName(a.ziel.month, lng) : "",
      });
    case "freigabe":
      return a.ziel.art === "freigabe_kampagne"
        ? (a.titel ?? t("aufgaben.text.freigabeKampagne"))
        : t("aufgaben.text.freigabeBesuch");
    case "datenluecke":
      // Die Art steht in der id (`datenluecke:projekt:…`), das Schema hat kein Feld dafür.
      return a.id.startsWith("datenluecke:projekt:")
        ? t("aufgaben.text.datenlueckeProjekt")
        : t("aufgaben.text.datenlueckeBeauftragung");
  }
}

/** Woher der Punkt kommt (zweite Zeile, nach dem Kunden). */
export function aufgabeHerkunft(a: Aufgabe, t: TFunction): string | null {
  if (a.antragsteller) return t("aufgaben.herkunft.antragsteller", { name: a.antragsteller });
  switch (a.quelle) {
    case "todo":
      return a.herkunft ? t("aufgaben.herkunft.todo", { datum: formatIsoDate(a.herkunft) }) : null;
    case "besuch":
      return a.herkunft
        ? t("aufgaben.herkunft.besuch", { datum: formatIsoDate(a.herkunft) })
        : t("aufgaben.herkunft.besuchNie");
    case "systemstand":
      return a.herkunft ? t("aufgaben.herkunft.systemstand", { datum: formatIsoDate(a.herkunft) }) : null;
    case "freigabe":
      return a.ziel.art === "freigabe_kampagne" ? t("aufgaben.herkunft.kampagne") : null;
    default:
      return null;
  }
}

function tageZwischen(von: string, heute: Date): number {
  const ms = fromLocalDateString(toLocalDateString(heute)).getTime() - fromLocalDateString(von).getTime();
  // Gerundet: über die Zeitumstellung hat ein Tag 23 oder 25 Stunden.
  return Math.round(ms / 86_400_000);
}

/** „seit N Tagen", ein Fälligkeitsdatum oder „ohne Frist". */
export function aufgabeFrist(a: Aufgabe, heute: Date, t: TFunction): string | null {
  if (a.faelligAm) return t("aufgaben.bis", { datum: formatIsoDate(a.faelligAm) });
  if (a.seit) {
    const tage = tageZwischen(a.seit, heute);
    return tage <= 0 ? t("aufgaben.seitHeute") : t("aufgaben.seitTagen", { count: tage });
  }
  return a.quelle === "datenluecke" ? t("aufgaben.ohneFrist") : null;
}

export interface ZielLink {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
}

/**
 * Wohin der Aktionsknopf führt. Besuche, Systemstand und Statusberichte
 * kennen keinen Suchparameter für den Kunden; der Sprung landet dort auf der
 * Seite, nicht auf der Zeile.
 */
export function zielLink(ziel: AufgabeZiel): ZielLink {
  switch (ziel.art) {
    case "weekly":
      return { to: "/weekly", search: { employeeId: ziel.leadEmployeeId, segmentId: ziel.segmentId } };
    case "kunde":
      return { to: "/customers/$id", params: { id: ziel.customerId } };
    case "besuche":
      return { to: "/visits" };
    case "systemstand":
      return { to: "/wartung" };
    case "statusbericht":
      return { to: "/statusberichte" };
    case "freigabe_besuch":
    case "freigabe_kampagne":
      return { to: "/inbox" };
    case "datenluecken":
      return { to: "/datenluecken" };
  }
}

function aktionKey(ziel: AufgabeZiel): string {
  if (ziel.art === "freigabe_besuch" || ziel.art === "freigabe_kampagne") return "freigabe";
  return ziel.art;
}

/** Die Inbox öffnen nur Admin, Bereichsleitung und Stellvertretung (router.ts). */
function zielErreichbar(ziel: AufgabeZiel, darfInbox: boolean): boolean {
  return darfInbox || !["freigabe_besuch", "freigabe_kampagne"].includes(ziel.art);
}

/** Zugänglicher Name einer Zeile: Text plus Kunde, damit Knöpfe in der Liste unterscheidbar bleiben. */
export function aufgabeWas(a: Aufgabe, t: TFunction, lng: string): string {
  return [aufgabeText(a, t, lng), a.kundeName].filter(Boolean).join(", ");
}

export function ZielKnopf({
  ziel,
  label,
  was,
}: {
  ziel: AufgabeZiel;
  label: string;
  was: string;
}) {
  const { t } = useTranslation();
  const l = zielLink(ziel);
  return (
    <Button
      component={Link}
      to={l.to as never}
      params={l.params as never}
      search={l.search as never}
      variant="default"
      size="xs"
      aria-label={t("aufgaben.aktionFuer", { aktion: label, was })}
      style={{ flexShrink: 0 }}
    >
      {label}
    </Button>
  );
}

export function AufgabeZeile({
  aufgabe: a,
  ueberfaellig,
  heute,
  darfInbox,
  onErledigen,
}: {
  aufgabe: Aufgabe;
  ueberfaellig: boolean;
  heute: Date;
  darfInbox: boolean;
  onErledigen: (todoId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const text = aufgabeText(a, t, i18n.language);
  const was = aufgabeWas(a, t, i18n.language);
  const zweiteZeile = [a.kundeName, aufgabeHerkunft(a, t)].filter(Boolean).join(" · ");
  const frist = aufgabeFrist(a, heute, t);
  const todoId = a.abhakbar ? a.todoId : null;

  return (
    <Group
      gap="md"
      wrap="wrap"
      px="md"
      py="sm"
      style={{ borderTop: "1px solid var(--msc-linie-fein)" }}
    >
      {todoId ? (
        <Checkbox
          aria-label={t("aufgaben.erledigt", { was })}
          checked={false}
          onChange={() => onErledigen(todoId)}
        />
      ) : (
        <Box w={20} aria-hidden />
      )}
      <QuelleTag quelle={a.quelle} />
      <Box style={{ flex: "1 1 260px", minWidth: 0 }}>
        <Text fw={500}>{text}</Text>
        {zweiteZeile ? (
          <Text size="sm" c="dimmed">
            {zweiteZeile}
          </Text>
        ) : null}
      </Box>
      {frist ? (
        <Text
          size="sm"
          fw={500}
          style={{ whiteSpace: "nowrap", color: ueberfaellig ? "var(--msc-dringend)" : undefined }}
          c={ueberfaellig ? undefined : "dimmed"}
        >
          {frist}
        </Text>
      ) : null}
      {zielErreichbar(a.ziel, darfInbox) ? (
        <ZielKnopf ziel={a.ziel} label={t(`aufgaben.aktion.${aktionKey(a.ziel)}`)} was={was} />
      ) : null}
    </Group>
  );
}
